/**
 * TdsFilterBar — one row of controls that scopes everything below it.
 *
 * The period is the thing every TDS screen is read through: a quarter's
 * transactions, its deductees, its challans and its return are all "the
 * same figures, narrowed". Keeping that choice in one bar at the top means
 * the narrowing is visible rather than remembered.
 *
 * Financial Year, Quarter, Month and Section apply as they are changed --
 * they narrow rows that are already loaded, so waiting for a button would
 * only add a step. Apply re-reads from the server, for when somebody else
 * has filed or paid something since the page was opened. That is the same
 * split the reference bar uses.
 */
import * as React from "react";
import FilterDropdown from "../../components/FilterDropdown";

/** Calendar months of each quarter of an Indian financial year. */
export const QUARTER_MONTHS: Record<number, number[]> = {
  1: [4, 5, 6],
  2: [7, 8, 9],
  3: [10, 11, 12],
  4: [1, 2, 3],
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

/**
 * The sections a salary deduction can be under. Teamlio's TDS is 24Q --
 * salary -- so this is short on purpose: 192 and the 2025 Act's reference
 * to the same charge. A longer list would offer sections the salary
 * template will not accept.
 */
const SECTION_OPTIONS = [
  { value: "", label: "All Sections" },
  { value: "192", label: "192 — Salaries" },
  { value: "392", label: "392 — Salaries (2025 Act)" },
];

export interface TdsFilters {
  /** 0 means every month of the quarter. */
  month: number;
  /** "" means every section. */
  section: string;
  /** "" means both. The portal takes a SEPARATE challan for resident and
   *  non-resident deductees, so this is a real split, not a label. */
  deducteeType: string;
  /** Which 24Q the page is about: the regular one, or a correction filed
   *  after it. They are separate returns for one quarter. */
  returnType: string;
}

export const EMPTY_FILTERS: TdsFilters = {
  month: 0, section: "", deducteeType: "", returnType: "regular",
};

const DEDUCTEE_TYPE_OPTIONS = [
  { value: "", label: "All (Resident + Non-Resident)" },
  { value: "resident", label: "Resident" },
  { value: "nonResident", label: "Non-Resident" },
];

const RETURN_TYPE_OPTIONS = [
  { value: "regular", label: "Regular" },
  { value: "correction", label: "Correction" },
];

function Field({ label, width, children }: {
  label: string; width?: number; children: React.ReactNode;
}) {
  return (
    <div style={{ width }}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function TdsFilterBar({
  financialYear, fyOptions, onFinancialYear,
  quarter, quarterOptions, onQuarter,
  filters, onFilters,
  onApply, refreshing,
}: {
  financialYear: string;
  fyOptions: { value: string; label: string }[];
  onFinancialYear: (value: string) => void;
  quarter: number;
  quarterOptions: { value: string; label: string }[];
  onQuarter: (value: number) => void;
  filters: TdsFilters;
  onFilters: (next: TdsFilters) => void;
  onApply: () => void;
  refreshing?: boolean;
}) {
  const months = QUARTER_MONTHS[quarter] || [];

  const monthOptions = [
    { value: "0", label: "All Months" },
    ...months.map((m) => ({ value: String(m), label: MONTH_NAMES[m - 1] })),
  ];

  const narrowed = filters.month !== 0 || filters.section !== ""
    || filters.deducteeType !== "" || filters.returnType !== "regular";

  return (
    <div className="gt-card p-4">
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Field label="Financial Year" width={132}>
          <FilterDropdown
            value={financialYear}
            onChange={onFinancialYear}
            searchable={false}
            ariaLabel="Financial Year"
            options={fyOptions}
          />
        </Field>

        <Field label="Quarter" width={160}>
          <FilterDropdown
            value={String(quarter)}
            // Changing the quarter moves the months under the Month filter,
            // so a month left selected from the old quarter would scope
            // everything to nothing. Cleared rather than carried over.
            onChange={(v) => { onQuarter(Number(v)); onFilters({ ...filters, month: 0 }); }}
            searchable={false}
            ariaLabel="Quarter"
            options={quarterOptions}
          />
        </Field>

        <Field label="Month" width={132}>
          <FilterDropdown
            value={String(filters.month)}
            onChange={(v) => onFilters({ ...filters, month: Number(v) })}
            searchable={false}
            ariaLabel="Month"
            options={monthOptions}
          />
        </Field>

        <Field label="Deductee Type" width={205}>
          <FilterDropdown
            value={filters.deducteeType}
            onChange={(v) => onFilters({ ...filters, deducteeType: v })}
            searchable={false}
            ariaLabel="Deductee Type"
            options={DEDUCTEE_TYPE_OPTIONS}
          />
        </Field>

        <Field label="Section" width={190}>
          <FilterDropdown
            value={filters.section}
            onChange={(v) => onFilters({ ...filters, section: v })}
            searchable={false}
            ariaLabel="Section"
            options={SECTION_OPTIONS}
          />
        </Field>

        <Field label="Return Type" width={150}>
          <FilterDropdown
            value={filters.returnType}
            onChange={(v) => onFilters({ ...filters, returnType: v })}
            searchable={false}
            ariaLabel="Return Type"
            options={RETURN_TYPE_OPTIONS}
          />
        </Field>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" }}>
          <button type="button" className="gt-btn is-primary"
            disabled={refreshing}
            title="Re-read this quarter from the server."
            onClick={onApply}>
            {refreshing ? "Applying…" : "Apply"}
          </button>
          <button type="button" className="gt-btn is-ghost"
            disabled={!narrowed}
            title={narrowed ? "Clear the month and section." : "Nothing is narrowed."}
            onClick={() => onFilters(EMPTY_FILTERS)}>
            Reset
          </button>
        </div>
      </div>

      {narrowed && (
        <p style={{ margin: "10px 0 0", fontSize: 11.5, color: "var(--gt-text-secondary)" }}>
          Showing {filters.month ? MONTH_NAMES[filters.month - 1] : "every month"}
          {filters.section ? `, section ${filters.section}` : ""}
          {filters.deducteeType
            ? `, ${filters.deducteeType === "resident" ? "resident" : "non-resident"} deductees`
            : ""}
          {filters.returnType !== "regular" ? ", correction return" : ""}
          {" "}of {financialYear} Q{quarter}. Totals below count only what is shown.
        </p>
      )}
    </div>
  );
}

/**
 * The month a row's tax belongs to, as a number, or 0 when it has no date.
 *
 * The DEDUCTION date decides, falling back to the credit date -- the same
 * reading the server matches challans by, because a deduction and the
 * payment it came out of can fall either side of a month end and the two
 * sides of the screen must not disagree about which month a row is in.
 *
 * Parsed off the string rather than through Date(): an ISO date put
 * through Date() is read as UTC and comes back a day earlier in IST, which
 * on the first of a month silently moves the row into the one before.
 */
export function entryMonth(entry: {
  deducted_on?: string | null; paid_on?: string | null;
}): number {
  const when = entry.deducted_on || entry.paid_on;
  if (!when) return 0;
  const month = Number(String(when).slice(5, 7));
  return Number.isFinite(month) ? month : 0;
}

/** Does this row survive the bar's narrowing? */
export function matchesFilters(
  entry: {
    deducted_on?: string | null; paid_on?: string | null;
    section?: string | null; deductee_type?: string | null;
  },
  filters: TdsFilters,
): boolean {
  // A row with no date cannot be placed in a month. It is left out of a
  // single month rather than guessed into one, and is still there under
  // All Months, where somebody will see it needs a date.
  if (filters.month && entryMonth(entry) !== filters.month) return false;
  if (filters.section && (entry.section || "") !== filters.section) return false;
  // Blank means both. A row with nothing recorded is resident, which is
  // what a salary deductee is unless the return says otherwise.
  if (filters.deducteeType
    && (entry.deductee_type || "resident") !== filters.deducteeType) return false;
  return true;
}
